/** Renders a selection mask into a reduced-resolution RGBA overlay so the operator can see what is selected. */
import {PixelBuffer} from './imageEditing';
import {SelectionMask} from './selectionMask';

/** Selection tools show tint and outline; other tools only the outline, so the tint does not hide their effect. */
export enum SelectionOverlayStyle {
   TintAndOutline = 'tintAndOutline',
   Outline = 'outline',
}

/** Longest overlay side in pixels; CSS scales the canvas up, which keeps slider updates cheap. */
export const OVERLAY_MAX_SIDE = 1600;

/** MUI primary blue at roughly 35 % opacity. */
const TINT = [25, 118, 210, 90];
const OUTLINE_DARK = [0, 0, 0, 255];
const OUTLINE_LIGHT = [255, 255, 255, 255];
const DASH_LENGTH = 6;

/** Neighbour offsets checked for the edge; two pixels deep so the outline survives CSS downscaling. */
const EDGE_OFFSETS = [[-1, 0], [1, 0], [0, -1], [0, 1], [-2, 0], [2, 0], [0, -2], [0, 2]];

/** Samples the mask (nearest neighbour) into a grid no larger than maxSide on its longest side. */
const sampleMask = (mask: SelectionMask, maxSide: number): SelectionMask => {
   const scale = Math.min(1, maxSide / Math.max(mask.width, mask.height));
   const width = Math.max(1, Math.round(mask.width * scale));
   const height = Math.max(1, Math.round(mask.height * scale));
   const sourceColumns = Array.from(
      {length: width},
      (_, x) => Math.min(mask.width - 1, Math.floor((x + 0.5) * mask.width / width))
   );
   const data = new Uint8Array(width * height);

   for (let y = 0; y < height; y += 1) {
      const sourceRow = Math.min(mask.height - 1, Math.floor((y + 0.5) * mask.height / height)) * mask.width;
      for (let x = 0; x < width; x += 1) {
         data[y * width + x] = mask.data[sourceRow + sourceColumns[x]];
      }
   }
   return {width, height, data};
};

/** Paints a translucent tint inside the selection and a dashed black/white outline along its edge. */
export const createSelectionOverlay = (
   mask: SelectionMask,
   maxSide: number,
   style: SelectionOverlayStyle
): PixelBuffer => {
   const sampled = sampleMask(mask, maxSide);
   const {width, height} = sampled;
   const data = new Uint8ClampedArray(width * height * 4);

   /** Pixels outside the overlay count as unselected, so a selection touching the border is outlined there. */
   const isSelected = (x: number, y: number): boolean =>
      x >= 0 && y >= 0 && x < width && y < height && sampled.data[y * width + x] === 1;

   for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
         if (!isSelected(x, y)) {
            continue;
         }
         const isEdge = EDGE_OFFSETS.some(([dx, dy]) => !isSelected(x + dx, y + dy));
         if (isEdge) {
            data.set(Math.floor((x + y) / DASH_LENGTH) % 2 === 0 ? OUTLINE_DARK : OUTLINE_LIGHT, (y * width + x) * 4);
         } else if (style === SelectionOverlayStyle.TintAndOutline) {
            data.set(TINT, (y * width + x) * 4);
         }
      }
   }
   return {width, height, data};
};
